import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Checkbox from '../../components/Input/Checkbox';
import Input from '../../components/Input/Input';
import Select from '../../components/Input/Select';
import TextArea from '../../components/Input/TextArea';
import Toast from '../../components/Toast/Toast';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { deleteProductAPI, getBrandsAPI, getCategoriesAPI, getProductByIdAPI, updateProductAPI } from "../../utils/api";
import { BASE_URL } from "../../utils/configConstants";
import "./AddProduct.css";
import { FaArrowLeft } from "react-icons/fa";
import RichTextInput from '../../components/Input/RichTextInput';

const EditProduct = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const catId = searchParams.get('catId');
  const navigate = useNavigate();
  // Product state
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state hooks - must be at top level
  const [productName, setProductName] = useState("");
  const [sku, setSku] = useState("");
  const [description, setDescription] = useState("");
  const [featured, setFeatured] = useState(false);
  const [onSale, setOnSale] = useState(false);
  const [returnPolicy, setReturnPolicy] = useState("");
  const [fabric, setFabric] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [BrandId, setBrandId] = useState("");
  const [colors, setColors] = useState([{ name: "", hex_code: "" }]);
  const [sizes, setSizes] = useState([]);
  const [active, setActive] = useState(false);

  // Video state
  const [video, setVideo] = useState(null);
  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [originalVideo, setOriginalVideo] = useState(null);
  const [removeExistingVideo, setRemoveExistingVideo] = useState(false);
  const [removeExistingThumbnail, setRemoveExistingThumbnail] = useState(false);

  // Categories and Brands state
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const [submitLoading, setSubmitLoading] = useState(false);

  // Toast state
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Toast function - must be defined before useEffect hooks
  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 2500);
  };



  // API call method for edit product operations
  const callEditProductAPI = async (apiFunction, requestData = null) => {
    try {
      let response;

      if (requestData) {
        response = await apiFunction(requestData);
      } else {
        response = await apiFunction();
      }

      // Check if response is valid
      if (response && typeof response === 'object') {
        return response;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console?.error('API call error:', error);
      throw error;
    }
  };

  // Fetch product by ID from API
  useEffect(() => {
    const fetchProduct = async () => {
      if (id) {
        try {
          setLoading(true);

          const response = await callEditProductAPI(getProductByIdAPI, id);

          if (response?.status === 200) {
            setProduct(response.data);
          } else if (response?.message) {
            setError(typeof response?.message === 'string'
              ? response?.message
              : 'Failed due to internal error');
          } else {
            setError('Failed due to internal error');
          }
        } catch (err) {
          setError('Failed due to internal error');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProduct();
  }, [id]);

  // Populate form with product data when product is loaded
  useEffect(() => {
    if (product) {
      setProductName(product?.name || "");
      setSku(product?.sku || "");
      setDescription(product?.description || "");
      setFeatured(product?.is_featured || false);
      setOnSale(product?.on_sale || false);
      setReturnPolicy(product?.return_policy || "");
      setFabric(product?.fabric || "");
      setCategoryId(product?.category_id || "");
      setBrandId(product?.brand_id || "");
      // Prepare colors, ensure only one 'No Color' with isNoColor: true
      let loadedColors = (product?.colors && product?.colors.length > 0)
        ? product?.colors.map(color => ({
          _id: color._id || color.hex_code || `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: color.name || "",
          hex_code: color.hex_code || ""
        }))
        : [];
      setColors(loadedColors);
      setSizes(product?.sizes || []);
      setActive(product?.active || false);

      // Set up video data
      if (product?.video) {
        setOriginalVideo(product.video);
        setVideo(null); // Don't set video file, just track original
        setVideoThumbnail(null); // Don't set thumbnail file, just track original
      } else {
        setOriginalVideo(null);
        setVideo(null);
        setVideoThumbnail(null);
      }
      // Reset remove flags when loading product
      setRemoveExistingVideo(false);
      setRemoveExistingThumbnail(false);
    }
  }, [product]);



  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await callEditProductAPI(getCategoriesAPI);

        if (response?.status === 200) {
          setCategories(response.data || []);
        } else if (response?.message) {
          console?.error('Error loading categories:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console?.error('Failed due to internal error');
        }
      } catch (err) {
        console?.error('Failed due to internal error');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch brands from API
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoadingBrands(true);
        const response = await callEditProductAPI(getBrandsAPI);

        if (response?.status === 200) {
          setBrands(response.data || []);
        } else if (response?.message) {
          console.error('Error loading brands:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console.error('Failed due to internal error');
        }
      } catch (err) {
        console.error('Failed due to internal error');
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);



  // Show loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Loading product...
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        {error}
      </div>
    );
  }

  // Show message if no product found
  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Product not found
      </div>
    );
  }

  // Use fetched categories and brands data
  const categoriesData = categories;
  const BrandData = brands;

  // Handle input change for color name or hex
  const handleColorChange = (idx, field, value) => {
    const updated = colors.map((color, i) =>
      i === idx ? { ...color, [field]: value } : color
    );
    setColors(updated);
  };

  // Add a new color input row
  const handleAddMoreColor = () => {
    setColors([...colors, { _id: '', name: '', hex_code: 'rgb(0, 0, 0)' }]);
  };

  const handleRemoveColor = (idx) => {
    setColors(colors.filter((_, i) => i !== idx));
  };

  const handleSizeChange = (idx, field, value) => {
    // Only allow numbers for wholesale_price, retail_price, mrp, and unit
    if (["wholesale_price", "retail_price", "mrp", "unit"].includes(field)) {
      if (!/^[0-9]*$/.test(value)) return;
    }
    const updated = sizes.map((row, i) =>
      i === idx ? { ...row, [field]: value } : row
    );
    setSizes(updated);
  };

  const handleAddSizeRow = () => {
    // Get the last size object or use empty values if no sizes exist yet
    const lastSize = sizes.length > 0 ? sizes[sizes.length - 1] : null;

    setSizes([...sizes, {
      _id: '',
      size: "",
      wholesale_price: lastSize?.wholesale_price || "",
      retail_price: lastSize?.retail_price || "",
      mrp: lastSize?.mrp || "",
      unit: lastSize?.unit || ""
    }]);
  };

  const handleRemoveSizeRow = (idx) => {
    setSizes(sizes?.filter((_, i) => i !== idx));
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader?.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle video thumbnail upload
  const handleVideoThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast("Invalid file format. Only PNG, JPG, JPEG, and WebP files are allowed for thumbnail.", "error");
      e.target.value = '';
      return;
    }

    // Validate file size (max 20MB)
    const maxSize = 40 * 1024 * 1024; // 40MB in bytes
    if (file.size > maxSize) {
      showToast(`File too large: ${file.name}. Maximum file size is 40MB.`, "error");
      e.target.value = '';
      return;
    }

    // Store file directly like AddProduct
    setVideoThumbnail(file);
    setRemoveExistingThumbnail(true);
    // Don't reset removeExistingThumbnail - if original was removed, it should stay removed
    // Once removeExistingThumbnail is true, it stays true until product is reloaded
  };

  // Handle video upload
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - only MP4, WebM, and MOV
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      showToast("Invalid file format. Only MP4, WebM and MOV files are allowed for video.", "error");
      e.target.value = '';
      return;
    }

    // Validate file size (max 20MB)
    const maxSize = 40 * 1024 * 1024; // 40MB in bytes
    if (file.size > maxSize) {
      showToast(`File too large: ${file.name}. Maximum file size is 40MB.`, "error");
      e.target.value = '';
      return;
    }

    // Store file directly like AddProduct
    setVideo(file);
    setRemoveExistingVideo(true);
    // Don't reset removeExistingVideo - if original was removed, it should stay removed
    // Once removeExistingVideo is true, it stays true until product is reloaded
  };

  // Remove video thumbnail (new upload)
  const handleRemoveVideoThumbnail = () => {
    setVideoThumbnail(null);
    // Reset thumbnail input
    const thumbnailInput = document.getElementById('video-thumbnail-input');
    if (thumbnailInput) thumbnailInput.value = '';
  };

  // Remove existing video thumbnail
  const handleRemoveExistingThumbnail = () => {
    setRemoveExistingThumbnail(true);
    // If user removes existing thumbnail, they might want to add new one
    // Reset thumbnail input to allow new upload
    const thumbnailInput = document.getElementById('video-thumbnail-input');
    if (thumbnailInput) thumbnailInput.value = '';
  };

  // Remove video (new upload)
  const handleRemoveVideo = () => {
    setVideo(null);
    // Reset video input
    const videoInput = document.getElementById('video-upload-input');
    if (videoInput) videoInput.value = '';
  };

  // Remove existing video
  const handleRemoveExistingVideo = () => {
    setRemoveExistingVideo(true);
    // If user removes existing video, they might want to add new one
    // Reset video input to allow new upload
    const videoInput = document.getElementById('video-upload-input');
    if (videoInput) videoInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitLoading(true);

    // Validation checks
    if (!productName.trim()) {
      showToast("Please enter the product name.");
      setSubmitLoading(false);
      return;
    }
    if (!categoryId) {
      showToast("Please select a category.");
      setSubmitLoading(false);
      return;
    }

    // Size validation - check for incomplete size entries
    const incompleteSizes = sizes.filter(row => {
      const hasSize = row.size?.trim();
      const hasWholesale = row.wholesale_price?.toString().trim();
      const hasRetail = row.retail_price?.toString().trim();
      const hasUnit = row.unit?.toString().trim();

      // Check if any field is filled but not all
      const filledFields = [hasSize, hasWholesale, hasRetail, hasUnit].filter(Boolean).length;
      return filledFields > 0 && filledFields < 4;
    });

    if (incompleteSizes.length > 0) {
      // Show common message for incomplete sizes
      showToast("Please complete all size fields");
      setSubmitLoading(false);
      return;
    }

    if (!sizes?.some(row => row.size && row.wholesale_price && row?.retail_price && row?.unit)) {
      showToast("Please add at least one size.");
      setSubmitLoading(false);
      return;
    }

    // Brand is optional - no validation needed
    // Color validation - check for incomplete color entries
    const colorsWithNameButNoHex = colors.filter(c => c.name?.trim() && !c.hex_code?.trim());
    const colorsWithHexButNoName = colors.filter(c => c.hex_code?.trim() && !c.name?.trim());

    if (colorsWithNameButNoHex.length > 0) {
      const colorNames = colorsWithNameButNoHex.map(c => c.name).join(', ');
      showToast(`Please enter hex code for color ${colorNames}`);
      setSubmitLoading(false);
      return;
    }

    if (colorsWithHexButNoName.length > 0) {
      const hexCodes = colorsWithHexButNoName.map(c => c.hex_code).join(', ');
      showToast(`Please enter color name for hex code ${hexCodes}`);
      setSubmitLoading(false);
      return;
    }

    // Check if at least one complete color is added
    const completeColors = colors.filter(c => c.name?.trim() && c.hex_code?.trim());
    if (completeColors.length === 0) {
      showToast("Please add at least one color with name and hex code.");
      setSubmitLoading(false);
      return;
    }

    // Video and thumbnail validation
    // User can update video or thumbnail independently - no need to require both

    // Check if thumbnail exists (either original not removed, or new thumbnail uploaded)
    const hasThumbnail = (!removeExistingThumbnail && originalVideo?.image) || videoThumbnail;

    // Check if video exists (either original not removed, or new video uploaded)
    const hasVideo = (!removeExistingVideo && originalVideo?.video) || video;

    // If original video was removed and no new video is uploaded, but thumbnail exists, show error
    if (removeExistingVideo && !video && hasThumbnail) {
      showToast("Please add a video.");
      setSubmitLoading(false);
      return;
    }

    // If original thumbnail was removed and no new thumbnail is uploaded, but video exists, show error
    if (removeExistingThumbnail && !videoThumbnail && hasVideo) {
      showToast("Please add a video thumbnail.");
      setSubmitLoading(false);
      return;
    }


    try {
      const formData = new FormData();

      formData.append('product_id', id);
      formData.append('name', productName);
      formData.append('sku', sku);
      formData.append('description', description);
      formData.append('is_featured', featured ? 1 : 0);
      formData.append('on_sale', onSale ? 1 : 0);
      formData.append('return_policy', returnPolicy);
      formData.append('fabric', fabric);
      formData.append('category_id', parseInt(categoryId));
      if (BrandId) {
        formData.append('brand_id', parseInt(BrandId));
      }
      formData.append('active', active ? 1 : 0);

      // Append colors as array
      const colorsData = colors.filter(c => c.name || c.hex_code);
      if (colorsData.length > 0) {
        colorsData.forEach((color) => {
          formData.append('colors[]', JSON.stringify({
            _id: color._id || "",
            name: color.name || "",
            hex_code: color.hex_code || ""
          }));
        });
      }

      // Append sizes as array
      const sizesData = sizes.filter(s => s.size && s.wholesale_price && s.retail_price && s.unit);
      if (sizesData.length > 0) {
        sizesData.forEach((row) => {
          formData.append('sizes[]', JSON.stringify({
            _id: row._id || "",
            size: row.size,
            retail_price: parseInt(row.retail_price),
            wholesale_price: parseInt(row.wholesale_price),
            unit: parseInt(row.unit),
            mrp: parseInt(row.mrp)
          }));
        });
      }

      if (videoThumbnail) {
        try {
          const thumbnailBase64 = await convertToBase64(videoThumbnail);
          formData.append('video_thumbnail', thumbnailBase64);
        } catch (error) {
          console.error('Error converting video thumbnail to base64:', error);
          showToast("Failed to process video thumbnail.", "error");
          setSubmitLoading(false);
          return;
        }
      }

      if (video) {
        formData.append('video', video);
      }

      if (!videoThumbnail && !video && removeExistingThumbnail && removeExistingVideo) {
        formData.append('remove_video', 1);
      }

      // console.log('Product Data (FormData):', {
      //   product_id: id,
      //   name: productName,
      //   description: description,
      //   is_featured: featured ? 1 : 0,
      //   on_sale: onSale ? 1 : 0,
      //   return_policy: returnPolicy,
      //   fabric: fabric,
      //   category_id: parseInt(categoryId),
      //   brand_id: BrandId ? parseInt(BrandId) : null,
      //   active: active ? 1 : 0,
      //   colors: colorsData,
      //   sizes: sizesData,
      //   has_video: !!video,
      //   has_video_thumbnail: !!videoThumbnail,
      //   remove_existing_video: removeExistingVideo,
      //   remove_existing_thumbnail: removeExistingThumbnail,
      //   original_video: originalVideo
      // });

      const response = await updateProductAPI(formData);

      if (response?.status === 200) {
        showToast("Product updated successfully.", "success");
        const url = catId
          ? `/product?categoryId=${catId}&refresh=true`
          : `/product?refresh=true`;
        navigate(url);
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const reqData = {
        product_id: productToDelete?._id
      };

      const response = await callEditProductAPI(deleteProductAPI, reqData);

      if (response?.status === 200) {
        setShowDeleteModal(false);
        setProductToDelete(null);
        showToast(`Product "${productToDelete?.name}" deleted successfully!`, "success");
        const url = catId
          ? `/product?categoryId=${catId}&refresh=true`
          : `/product?refresh=true`;
        navigate(url);
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  return (
    <>
      <Toast toast={toast} />
      <div className="product-main-content">
        <div className="order-details-header">
          <button className="back-button" onClick={() => {
            const url = catId
              ? `/product?categoryId=${catId}`
              : `/product`;
            navigate(url);
          }}>
            <FaArrowLeft /> Back to Products
          </button>
        </div>
        <form onSubmit={handleSubmit} className="product-form">
          <TextArea
            title="Product Name"
            value={productName}
            onChange={e => setProductName(e.target.value)}
            placeholder="Enter product name"
            required
            maxLength={200}
            rows={3}
          />

          <Input
            title="SKU"
            value={sku}
            onChange={e => setSku(e.target.value)}
            placeholder="Enter SKU"
            required
            maxLength={100}
          />

          <RichTextInput
            title="Description"
            value={description}
            onChange={setDescription}
            placeholder="Enter product description"
            rows={6}
          />

          <RichTextInput
            title="Return Policy"
            value={returnPolicy}
            onChange={setReturnPolicy}
            placeholder="Enter return policy..."
            rows={6}
          />

          <Input
            title="Fabric"
            type="text"
            value={fabric}
            onChange={e => setFabric(e.target.value)}
            placeholder="Enter fabric type (e.g. Cotton, Silk)"
            maxLength={200}
          />

          <Select
            title="Category"
            value={categoryId}
            onChange={e => setCategoryId(e.target.value)}
            placeholder={loadingCategories ? "Loading categories..." : "Select category"}
            required
            disabled={loadingCategories}
            options={categoriesData.map(cat => ({ value: cat._id, label: cat.name }))}
          />

          <Select
            title="Brand"
            value={BrandId}
            onChange={e => setBrandId(e.target.value)}
            placeholder={loadingBrands ? "Loading brands..." : "Select Brand"}
            disabled={loadingBrands}
            options={BrandData.map(brand => ({ value: brand._id, label: brand.name }))}
          />

          <Checkbox
            title="Featured"
            checked={featured}
            onChange={e => setFeatured(e.target.checked)}
          />
          <Checkbox
            title="Sale"
            checked={onSale}
            onChange={e => setOnSale(e.target.checked)}
          />

          <div className="size-section">
            <h3 className="size-title">Size</h3>
            <div className="size-header">
              <div className="size-header-item">SIZE</div>
              <div className="size-header-item">WHOLESALE PRICE</div>
              <div className="size-header-item">RETAIL PRICE</div>
              <div className="size-header-item">MRP</div>
              <div className="size-header-item">UNIT</div>
              <div className="remove-price-header-placeholder"></div>
            </div>
            {sizes.map((row, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  className="size-textarea"
                  placeholder="Size"
                  value={row.size}
                  onChange={e => handleSizeChange(idx, "size", e.target.value)}
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Wholesale Price"
                  value={row.wholesale_price}
                  onChange={e => handleSizeChange(idx, "wholesale_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Retail Price"
                  value={row.retail_price}
                  onChange={e => handleSizeChange(idx, "retail_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="MRP"
                  value={row.mrp}
                  onChange={e => handleSizeChange(idx, "mrp", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Unit"
                  value={row.unit}
                  onChange={e => handleSizeChange(idx, "unit", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {sizes.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveSizeRow(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-price-header-placeholder"></div>
                )}
              </div>
            ))}
            <div className="add-price-btn-container">
              <button
                type="button"
                className="add-price-btn"
                onClick={handleAddSizeRow}
                title="Add Size"
              >
                Add More
              </button>
            </div>
          </div>
          {/* Color Section */}
          <div className="size-section">
            <h3 className="size-title">Color</h3>
            <div className="size-header">
              <div className="size-header-item">COLOR NAME</div>
              <div className="size-header-item">COLOR PICKER</div>
              <div className="size-header-item">HEX CODE</div>
              <div className="remove-size-header-placeholder"></div>
            </div>
            {colors.map((color, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  placeholder="Color name"
                  value={color.name}
                  onChange={e => handleColorChange(idx, "name", e.target.value)}
                  className="size-textarea"
                  disabled={color.isNoColor}
                  maxLength={255}
                />
                <div className="color-picker-input-container">
                  <input
                    type="color"
                    value={color.hex_code === "rgba(0, 0, 0, 0)" ? "#000000" : color.hex_code || "#000000"}
                    onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                    className="color-picker-input"
                    disabled={color.isNoColor}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Hex code"
                  value={color.hex_code}
                  onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                  className="size-textarea"
                  maxLength={7}
                  disabled={color.isNoColor}
                />
                {!color.isNoColor && colors.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveColor(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-color-btn-placeholder"></div>
                )}
              </div>
            ))}
            <div className="add-size-btn-container">
              <button
                type="button"
                onClick={handleAddMoreColor}
                className="add-price-btn"
                title="Add Color"
              >
                Add More
              </button>
            </div>
          </div>

          {/* Video Section */}
          <div className="size-section">
            <h3 className="size-title" style={{ textAlign: 'left' }}>Add Video</h3>
            <div className="video-section-container" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
              {/* Video Thumbnail */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.75rem',
                  fontWeight: 700,
                  color: '#475569',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  textAlign: 'left',
                }}>
                  Thumbnail
                </label>
                <div className="image-boxes-container" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {videoThumbnail && (
                    <div className="image-preview-item">
                      <img
                        src={URL.createObjectURL(videoThumbnail)}
                        alt="video-thumbnail"
                        className="image-preview"
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0'
                        }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemoveVideoThumbnail}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                  {!videoThumbnail && !removeExistingThumbnail && originalVideo?.image && (
                    <div className="image-preview-item">
                      <img
                        src={originalVideo.image.startsWith('data:') ? originalVideo.image : BASE_URL + originalVideo.image}
                        alt="video-thumbnail"
                        className="image-preview"
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0'
                        }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemoveExistingThumbnail}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                  <div
                    className="image-upload-box"
                    onClick={() => document.getElementById('video-thumbnail-input').click()}
                    style={{
                      width: '200px',
                      height: '150px'
                    }}
                  >
                    <span className="plus-icon">+</span>
                  </div>
                </div>
                <input
                  id="video-thumbnail-input"
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,image/png,image/jpg,image/jpeg,image/webp"
                  onChange={handleVideoThumbnailChange}
                  style={{ display: "none" }}
                />
              </div>

              {/* Video */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.75rem',
                  fontWeight: 700,
                  color: '#475569',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  textAlign: 'left',
                  width: '100%'
                }}>
                  Video
                </label>
                <div className="image-boxes-container" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {video && (
                    <div className="image-preview-item" style={{ position: 'relative' }}>
                      <video
                        src={URL.createObjectURL(video)}
                        controls
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0'
                        }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemoveVideo}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                  {!video && !removeExistingVideo && originalVideo?.video && (
                    <div className="image-preview-item" style={{ position: 'relative' }}>
                      <video
                        src={originalVideo.video.startsWith('data:') || originalVideo.video.startsWith('http') ? originalVideo.video : BASE_URL + originalVideo.video}
                        controls
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0'
                        }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemoveExistingVideo}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                  <div
                    className="image-upload-box"
                    onClick={() => document.getElementById('video-upload-input').click()}
                    style={{
                      width: '200px',
                      height: '150px'
                    }}
                  >
                    <span className="plus-icon">+</span>
                  </div>
                </div>
                <input
                  id="video-upload-input"
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                  onChange={handleVideoChange}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </div>

          <div className="submit-btn-row">
            <button
              type="submit"
              className="submit-btn"
              disabled={submitLoading}
            >
              {submitLoading ? "Updating..." : "Update Product"}
            </button>
          </div>
        </form>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          title="Delete Product"
          message={`Are you sure you want to delete "${productToDelete?.name}"?`}
          confirmText="Delete"
        />
      </div>
    </>
  );
};

export default EditProduct; 